import dayjs from '~/utils/dayjsConfig';
import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import { productionApi } from '~/api/production';
import { Button } from '~/shadcn/components/ui/Button';
import { getNextWeekWednesday, getWeekNumber, getWeekYear } from '~/utils/dateUtils/dateUtils';

import DateWeekInput from '../components/DateWeekInput';
import PackGroupInput from '../components/PackGroupInput';
import useFilteredAndSortedRows from '../utils/useFilteredAndSortedRows';
import usePagination from '../utils/usePagination';

import ConfigTable from './components/ConfigTable';
import DuplicateDialog from '../components/DuplicateDialog';
import { generateSlug } from '../StorefrontConfigEditor/utils/slugUtils';

/**
 * Interface for managing and displaying storefront configurations
 * Provides functionality for viewing, creating, duplicating, and deleting configurations
 *
 * @returns {JSX.Element} StorefrontConfigList component with configuration management interface
 */
function StorefrontConfigList() {
    const history = useHistory();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);

    const queryDateString = queryParams.get('date');
    const queryPackGroupIDString = queryParams.get('packGroupID');

    const today = new Date();
    const isTuesday = today.getDay() === 2;

    let initialDate = today;
    if (queryDateString) {
        initialDate = new Date(queryDateString + 'T00:00:00');
    } else if (isTuesday) {
        initialDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
    }

    const [configs, setConfigs] = useState();
    const [packGroups, setPackGroups] = useState();
    const [sellableBundles, setSellableBundles] = useState();
    const [sellableItems, setSellableItems] = useState();

    const [date, setDate] = useState(initialDate);
    const [datePopoverOpen, setDatePopoverOpen] = useState(false);
    const [selectedPackGroupID, setSelectedPackGroupID] = useState(queryPackGroupIDString);

    const [searchQuery, setSearchQuery] = useState('');
    const [sortState, setSortState] = useState({ key: null, direction: null });
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [rowsPerPage, setRowsPerPage] = useState('10');
    const [currentPage, setCurrentPage] = useState(1);

    const [duplicateDialogConfig, setDuplicateDialogConfig] = useState(null);
    const [duplicateLoading, setDuplicateLoading] = useState(false);

    const filteredAndSortedRows = useFilteredAndSortedRows(configs, searchQuery, sortState);
    const { currentRows, totalPages } = usePagination(filteredAndSortedRows, rowsPerPage, currentPage);

    const initialLoadRef = useRef(true);

    useEffect(() => {
        const fetchPackGroups = async () => {
            try {
                const response = await productionApi.getPackGroups({
                    week: getWeekNumber(date, 'YYYY-MM-DD'),
                    year: getWeekYear(date, 'YYYY-MM-DD'),
                });

                const packGroups = response.data.packGroups;

                // Filter out non-standard pack groups (ex. "LS CHI1", "ST CHI1", "SM CHI1")
                const filteredPackGroups = packGroups.filter((packGroup) => packGroup.name.split(' ').length === 1);

                setPackGroups(filteredPackGroups);

                if (!packGroups.length) {
                    setSelectedPackGroupID(null);
                    return;
                }

                const queryPackGroupID = Number(queryPackGroupIDString);
                const useQueryPackGroupID =
                    initialLoadRef.current && packGroups.some((packGroup) => packGroup.id === queryPackGroupID);

                if (useQueryPackGroupID) {
                    setSelectedPackGroupID(queryPackGroupID);
                } else {
                    const defaultPackGroupID = packGroups[0].id;
                    setSelectedPackGroupID(defaultPackGroupID);
                    updateQueryParams({
                        date: date.toISOString().split('T')[0],
                        packGroupID: defaultPackGroupID,
                    });
                }

                initialLoadRef.current = false;
            } catch (error) {
                console.error('Error fetching pack groups:', error);
            }
        };
        fetchPackGroups();
    }, [date]);

    useEffect(() => {
        if (!selectedPackGroupID) return;

        const fetchConfigs = async () => {
            try {
                const response = await productionApi.getStorefrontConfigurationsByPackGroupID(selectedPackGroupID);
                setConfigs(response.data.configurations);
            } catch (error) {
                console.error('Error fetching configurations:', error);
            }
        };

        fetchConfigs();
    }, [selectedPackGroupID]);

    useEffect(() => {
        if (!selectedPackGroupID) return;

        const fetchSellableBundles = async () => {
            try {
                const response = await productionApi.getSellableBundles(selectedPackGroupID);
                setSellableBundles(response.data.sellableBundles);
            } catch (error) {
                console.error('Error fetching configuration:', error);
            }
        };

        const fetchSellableItems = async () => {
            try {
                const response = await productionApi.getSellableItems(selectedPackGroupID);
                setSellableItems(response.data.sellableItems);
            } catch (error) {
                console.error('Error fetching configuration:', error);
            }
        };

        fetchSellableBundles();
        fetchSellableItems();
    }, [selectedPackGroupID]);

    const updateQueryParams = (params) => {
        const currentParams = new URLSearchParams(location.search);
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                currentParams.set(key, value);
            } else {
                currentParams.delete(key);
            }
        });
        history.replace({ search: currentParams.toString() });
    };

    const handleSelectDate = (date) => {
        setDate(date);
        setDatePopoverOpen(false);
        updateQueryParams({ date: date.toISOString().split('T')[0] });
    };

    const handleSelectPackGroup = (packGroupID) => {
        setSelectedPackGroupID(packGroupID);
        updateQueryParams({ packGroupID });
    };

    const deleteConfigs = async (configIds) => {
        const multiple = configIds.length > 1;
        if (!confirm(`Are you sure you want to delete ${multiple ? 'these configurations' : 'this configuration'}?`)) {
            return;
        }
        try {
            await Promise.all(configIds.map((id) => productionApi.deleteStorefrontConfiguration(id)));
            toast.success(`Configuration${multiple ? 's' : ''} deleted successfully!`);
            setSelectedRows(new Set());
            await productionApi.getStorefrontConfigurationsByPackGroupID(selectedPackGroupID);
        } catch (error) {
            console.error('Error deleting configuration:', error);
        }
    };

    const handleSelectAll = () => {
        if (selectedRows.size === currentRows.length) {
            setSelectedRows(new Set());
        } else {
            const updatedSelectedRows = new Set(selectedRows);
            currentRows.forEach((item) => {
                updatedSelectedRows.add(item.id);
            });
            setSelectedRows(updatedSelectedRows);
        }
    };

    const handleSelectRow = (itemId) => {
        setSelectedRows((prev) => {
            const updatedSelectedRows = new Set(prev);
            if (updatedSelectedRows.has(itemId)) {
                updatedSelectedRows.delete(itemId);
            } else {
                updatedSelectedRows.add(itemId);
            }
            return updatedSelectedRows;
        });
    };

    const handleSort = (key, direction) => {
        setSortState({ key, direction });
    };

    const handleDuplicateConfig = async (name, packGroupIDs) => {
        try {
            setDuplicateLoading(true);
            await Promise.all(
                packGroupIDs.map(async (packGroupID) => {
                    const originalConfigRes = await productionApi.getStorefrontConfigurationByID(
                        duplicateDialogConfig.id,
                    );
                    const originalConfig = originalConfigRes.data.storefrontConfiguration;

                    const duplicateData = {
                        ...originalConfig,
                        id: null,
                        name,
                        packGroupID,
                        published: false,
                        slices: originalConfig.slices.map((slice) => {
                            const cleanedSlice = {
                                ...slice,
                                id: null,
                                ...(slice.carouselID && { carouselID: null }),
                            };

                            if (slice.name) {
                                cleanedSlice.slug = generateSlug(slice.name);
                            }

                            return cleanedSlice;
                        }),
                    };

                    await productionApi.saveStorefrontConfiguration(duplicateData);
                }),
            );

            const newConfigsRes = await productionApi.getStorefrontConfigurationsByPackGroupID(selectedPackGroupID);
            setConfigs(newConfigsRes.data.configurations);
            setDuplicateDialogConfig(null);
            toast.success('Configuration duplicated successfully!');
        } catch (error) {
            console.error('Error duplicating configuration:', error);
            toast.error('Failed to duplicate configuration');
        } finally {
            setDuplicateLoading(false);
        }
    };

    const handleRowsPerPageChange = (rows) => {
        setRowsPerPage(rows);
        setCurrentPage(1);
    };

    const getDisabledPackGroupDates = (date) => dayjs(date).isSameOrAfter(getNextWeekWednesday(), 'day');

    return (
        <section className="flex flex-col items-start gap-4 w-full max-w-7xl mx-auto pt-2">
            <h1 className="text-2xl font-bold">Storefront Configurations</h1>

            <div className="flex justify-between w-full">
                <div className="flex gap-2">
                    <DateWeekInput
                        date={date}
                        disabledDates={getDisabledPackGroupDates}
                        onSelectDate={handleSelectDate}
                        popoverOpen={datePopoverOpen}
                        setPopoverOpen={setDatePopoverOpen}
                    />

                    <PackGroupInput
                        packGroups={packGroups}
                        selectedPackGroupID={selectedPackGroupID}
                        setSelectedPackGroupID={handleSelectPackGroup}
                    />
                </div>

                <Button
                    disabled={!selectedPackGroupID}
                    onClick={() =>
                        history.push(`/production/storefront-configurations/new?packGroupID=${selectedPackGroupID}`)
                    }
                >
                    Create Configuration
                </Button>
            </div>
            <ConfigTable
                configs={configs}
                currentPage={currentPage}
                currentRows={currentRows}
                deleteConfigs={deleteConfigs}
                handleSelectRow={handleSelectRow}
                onDuplicateConfig={(config) => setDuplicateDialogConfig(config)}
                onPageChange={setCurrentPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                onSearchQueryChange={(e) => setSearchQuery(e.target.value)}
                onSelectAll={handleSelectAll}
                onSelectRow={handleSelectRow}
                onSort={handleSort}
                rowsPerPage={rowsPerPage}
                searchQuery={searchQuery}
                selectedRows={selectedRows}
                sortState={sortState}
                totalPages={totalPages}
                validProducts={[...(sellableBundles ?? []), ...(sellableItems ?? [])]}
            />
            <DuplicateDialog
                configId={duplicateDialogConfig?.id}
                initialDate={date}
                initialName={duplicateDialogConfig?.name}
                isOpen={!!duplicateDialogConfig}
                isLoading={duplicateLoading}
                onClose={() => setDuplicateDialogConfig(null)}
                onConfirm={handleDuplicateConfig}
            />
        </section>
    );
}

export default StorefrontConfigList;
